/**
 * Pont entre le client HTTP (hors React) et la modale de session expirée (React).
 * Sur 401 mid-session, `httpClient` émet l'événement ; si un écouteur le prend en
 * charge (`preventDefault`), il affiche la modale plutôt qu'une redirection brute.
 */
export const SESSION_EXPIRED_EVENT = 'cse:session-expired'

export type SessionExpiredReason = 'session' | 'csrf'

export type SessionExpiredEventDetail = {
  reason: SessionExpiredReason
}

/** Émet l'événement. Renvoie `true` si un écouteur l'a pris en charge (`preventDefault`). */
export function emitSessionExpired(reason: SessionExpiredReason = 'session'): boolean {
  if (typeof window === 'undefined') {
    return false
  }
  const event = new CustomEvent<SessionExpiredEventDetail>(SESSION_EXPIRED_EVENT, {
    cancelable: true,
    detail: { reason },
  })
  window.dispatchEvent(event)
  return event.defaultPrevented
}

/** Abonne un handler à l'événement de session expirée. Renvoie la fonction de désabonnement. */
export function onSessionExpired(handler: (event: Event) => void): () => void {
  window.addEventListener(SESSION_EXPIRED_EVENT, handler)
  return () => window.removeEventListener(SESSION_EXPIRED_EVENT, handler)
}

export function sessionExpiredReasonFromEvent(event: Event): SessionExpiredReason {
  if ('detail' in event && event.detail && typeof event.detail === 'object') {
    const reason = (event.detail as SessionExpiredEventDetail).reason
    if (reason === 'csrf') {
      return 'csrf'
    }
  }
  return 'session'
}
